import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, Loader2, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useBadgeTypesQuery } from '@/client/api/badges/badges.query';
import {
  useAwardBadgeMutation,
  useRevokeBadgeMutation,
} from '@/client/api/badges/badges.mutation';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

const selectCls =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const BadgeAwardPage = () => {
  useBreadcrumbs([
    { label: 'Badges', href: '/admin/badges' },
    { label: 'Award / Revoke' },
  ]);

  const { data: types = [], isLoading: typesLoading } = useBadgeTypesQuery(true);

  const awardMutation = useAwardBadgeMutation();
  const revokeMutation = useRevokeBadgeMutation();

  const [award, setAward] = useState({
    btid: '',
    target: 'uid' as 'uid' | 'oid',
    targetId: '',
    notes: '',
    skip_verification: true,
  });

  const [revoke, setRevoke] = useState({
    kind: 'ubid' as 'ubid' | 'obid',
    id: '',
    notes: '',
  });

  const handleAward = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!award.btid || !award.targetId.trim()) {
      toast.error('Badge type and recipient are required');
      return;
    }

    const toastId = toast.loading('Awarding badge…');
    try {
      await awardMutation.mutateAsync({
        btid: award.btid,
        ...(award.target === 'uid'
          ? { uid: award.targetId.trim() }
          : { oid: award.targetId.trim() }),
        notes: award.notes.trim() || undefined,
        skip_verification: award.skip_verification ? 1 : 0,
      });
      toast.success('Badge awarded', {
        id: toastId,
        description: 'Direct grant completed.',
        richColors: true,
      });
      setAward((prev) => ({ ...prev, targetId: '', notes: '' }));
    } catch (error: any) {
      toast.error('Award failed', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
        richColors: true,
      });
    }
  };

  const handleRevoke = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!revoke.id.trim()) {
      toast.error('Awarded badge id is required');
      return;
    }
    if (!revoke.notes.trim()) {
      toast.error('A reason is required to revoke');
      return;
    }

    const toastId = toast.loading('Revoking badge…');
    try {
      await revokeMutation.mutateAsync({
        ...(revoke.kind === 'ubid'
          ? { ubid: revoke.id.trim() }
          : { obid: revoke.id.trim() }),
        notes: revoke.notes.trim(),
      });
      toast.success('Badge revoked', {
        id: toastId,
        richColors: true,
      });
      setRevoke({ kind: 'ubid', id: '', notes: '' });
    } catch (error: any) {
      toast.error('Revoke failed', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
        richColors: true,
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
              <Award className="h-6 w-6 text-amber-600" />
              Award / Revoke
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Direct admin grant or revoke without an application. Skip verification defaults on
              for intentional manual overrides.
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/admin/badges?status=under_review">Review queue</Link>
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Award */}
          <form
            onSubmit={handleAward}
            className="space-y-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm"
          >
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Award className="h-5 w-5 text-amber-600" />
              Award badge
            </h2>

            <div className="space-y-1">
              <Label htmlFor="award-btid">Badge type</Label>
              <select
                id="award-btid"
                className={selectCls}
                value={award.btid}
                onChange={(e) => setAward((p) => ({ ...p, btid: e.target.value }))}
                disabled={typesLoading}
                required
              >
                <option value="">Select type…</option>
                {types.map((t) => (
                  <option key={t.btid} value={String(t.btid)}>
                    {t.name} (btid {t.btid})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="award-target">Recipient</Label>
                <select
                  id="award-target"
                  className={selectCls}
                  value={award.target}
                  onChange={(e) =>
                    setAward((p) => ({
                      ...p,
                      target: e.target.value as 'uid' | 'oid',
                    }))
                  }
                >
                  <option value="uid">User (uid)</option>
                  <option value="oid">Organization (oid)</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="award-id">{award.target === 'uid' ? 'uid' : 'oid'}</Label>
                <Input
                  id="award-id"
                  value={award.targetId}
                  onChange={(e) => setAward((p) => ({ ...p, targetId: e.target.value }))}
                  placeholder={award.target === 'uid' ? 'User id' : 'Org id'}
                  required
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="award-notes">Notes</Label>
              <textarea
                id="award-notes"
                rows={3}
                className={selectCls}
                value={award.notes}
                onChange={(e) => setAward((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Optional context for the grant"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={award.skip_verification}
                onChange={(e) =>
                  setAward((p) => ({ ...p, skip_verification: e.target.checked }))
                }
              />
              Skip verification (default for manual grants)
            </label>

            <Button type="submit" disabled={awardMutation.isPending} className="w-full gap-2">
              {awardMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Award className="h-4 w-4" />
              )}
              Award badge
            </Button>
          </form>

          {/* Revoke */}
          <form
            onSubmit={handleRevoke}
            className="space-y-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm"
          >
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Ban className="h-5 w-5 text-red-600" />
              Revoke badge
            </h2>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="revoke-kind">Awarded id type</Label>
                <select
                  id="revoke-kind"
                  className={selectCls}
                  value={revoke.kind}
                  onChange={(e) =>
                    setRevoke((p) => ({
                      ...p,
                      kind: e.target.value as 'ubid' | 'obid',
                    }))
                  }
                >
                  <option value="ubid">User badge (ubid)</option>
                  <option value="obid">Org badge (obid)</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="revoke-id">{revoke.kind}</Label>
                <Input
                  id="revoke-id"
                  value={revoke.id}
                  onChange={(e) => setRevoke((p) => ({ ...p, id: e.target.value }))}
                  placeholder={revoke.kind}
                  required
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="revoke-notes">
                Reason <span className="text-red-500">*</span>
              </Label>
              <textarea
                id="revoke-notes"
                rows={3}
                className={selectCls}
                value={revoke.notes}
                onChange={(e) => setRevoke((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Why is this badge being revoked?"
                required
              />
            </div>

            <Button
              type="submit"
              variant="outline"
              disabled={revokeMutation.isPending}
              className="w-full gap-2 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              {revokeMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Ban className="h-4 w-4" />
              )}
              Revoke badge
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BadgeAwardPage;
